<!--
  Muestra el documento HTML en un marco aislado con permiso para ejecutar scripts, sin
  acceso a la sesión ni al resto del panel. Intercambia mensajes con el puente para
  enviar el tema, la identidad y los resultados de datos.

  runOp delega las operaciones al servidor, que comprueba el acceso y las fuentes
  declaradas. MAX_RESTORES limita los intentos de restaurar un documento que navega fuera
  del marco. En modo ensayo se bloquean las operaciones de escritura.

  El oyente de mensajes se registra una vez y lee las variables actuales del componente.
-->
<script lang="ts" module>
  import { SPACE_VARS, TEXT_VARS, THEME_VARS } from "@shared/htmlContract";
  import { SourceError } from "@shared/htmlSources";
  import type { AppPerson, HtmlSource, ProbeRead, ProbeView } from "@shared/types";
  import type PocketBase from "pocketbase";

  /** Alto de partida. Pequeño a propósito: el documento crece hasta el suyo. */
  const START_HEIGHT = 160;

  /**
   * Vueltas seguidas que se le consienten a un documento que se va.
   *
   * Devolverlo a su sitio arregla al que se va por accidente. Al que se va
   * siempre --un guardia de sesión que nunca encuentra su token-- no: volveria
   * a irse en cada vuelta, y ese ciclo se lo come el navegador de quien mira.
   */
  const MAX_RESTORES = 3;

  /** Tiempo que ha de aguantar un documento para que las vueltas no cuenten. */
  const SETTLED_MS = 5000;

  /** Las órdenes que dejan rastro. En ensayo no se dejan pasar. */
  const WRITE_OPS = new Set(["crear", "actualizar", "borrar"]);

  interface OpContext {
    sources: HtmlSource[];
    client: PocketBase;
    appId: string;
    pageId: string;
    /** Rol con el que mirar la página, para la vista previa del constructor. */
    previewRole?: string;
    /** Persona concreta con la que mirar, de entre las que tienen ese rol. */
    previewPerson?: AppPerson | null;
    /** Mirar la página como quien llega sin cuenta. */
    previewAnon?: boolean;
    /** Mirar la página como quien entró y no tiene ningún rol. */
    previewNoRole?: boolean;
    /**
     * Ensayo: lo que escribe un conector no sale y se contesta con su
     * respuesta de ejemplo. Lo decide el servidor, que sabe qué operación
     * escribe.
     */
    dryRun?: boolean;
  }

  /**
   * Un error del servidor que trae su código, para poder distinguirlo.
   *
   * El puente ya lo cuelga del error que le entrega al documento; aquí hace
   * falta el mismo dato para poner el aviso desde la plataforma.
   */
  class OpError extends SourceError {
    codigo: string;

    constructor(message: string, codigo: string) {
      super(message);
      this.codigo = codigo;
    }
  }

  /**
   * Envía la operación al servidor, que resuelve la sesión y comprueba el acceso antes de
   * entregar datos.
   *
   * Incluye el manifiesto para permitir pruebas de documentos aún no guardados. El
   * servidor solo acepta esas fuentes enviadas si la petición pertenece a quien puede
   * construir la aplicación.
   */
  async function runOp(op: string, args: unknown[], ctx: OpContext): Promise<unknown> {
    if (!ctx.appId || !ctx.pageId) {
      throw new SourceError("Esta vista previa no está conectada a ninguna página guardada.");
    }

    const headers = new Headers({ "content-type": "application/json" });
    if (ctx.client.authStore.token) headers.set("authorization", ctx.client.authStore.token);

    const res = await fetch(`/api/apps/${ctx.appId}/paginas/${ctx.pageId}/datos`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        op,
        args,
        rol: ctx.previewRole || undefined,
        persona: ctx.previewPerson?.id || undefined,
        sinSesion: ctx.previewAnon || undefined,
        sinRol: ctx.previewNoRole || undefined,
        fuentes: ctx.sources,
        prueba: ctx.dryRun || undefined,
      }),
    });

    const data = jsonBody(await res.text());
    if (!res.ok) {
      const message = data?.error ?? `El servidor respondio ${res.status}`;
      throw data?.codigo ? new OpError(message, String(data.codigo)) : new SourceError(message);
    }
    return data;
  }

  /**
   * Lo que una lectura trajo, contado para la prueba: cuantas filas llegaron,
   * cuantas cumplian y si se pidió con filtro. Las escrituras no cuentan.
   */
  function readOf(op: string, args: unknown, result: unknown): ProbeRead | null {
    if (op !== "listar" && op !== "contar" && op !== "obtener") return null;
    const list = Array.isArray(args) ? args : [];
    const opts = (list[1] && typeof list[1] === "object" ? list[1] : {}) as Record<string, unknown>;
    const body = (result && typeof result === "object" ? result : {}) as Record<string, unknown>;
    const filas = Array.isArray(body.filas)
      ? body.filas.length
      : op === "obtener" && result
        ? 1
        : 0;
    return {
      op,
      tabla: String(list[0] ?? ""),
      filas,
      total: Number(body.total ?? filas) || 0,
      filtrada:
        op !== "obtener" &&
        Boolean(
          (opts.filtro && Object.keys(opts.filtro as object).length) ||
          String(opts.buscar ?? "").trim(),
        ),
    };
  }

  /**
   * El tema tal como queda resuelto aquí, listo para el documento: los colores
   * de la aplicación y las medidas de la casa. Van juntos a propósito, para que
   * una página se dibuje con el mismo aspecto que el panel sin copiar valores.
   */
  function readTheme(element: Element): {
    vars: Record<string, string>;
    modo: "light" | "dark";
    paleta: string | null;
  } {
    const computed = getComputedStyle(element);
    const vars: Record<string, string> = {};
    for (const name of [...THEME_VARS, ...SPACE_VARS, ...TEXT_VARS]) {
      const value = computed.getPropertyValue(name).trim();
      if (value) vars[name] = value;
    }
    /*
     * Los que no son color ni medida, y que el documento necesita para
     * derivar por su cuenta: el color crudo de una paleta a mano --que no
     * está en ningún `[data-palette]` de la hoja-- y el tamaño de letra que
     * aquí resuelve el contenedor --el de la aplicación sobre el del panel--,
     * que allí tiene que llevarlo la raíz para que el rem valga lo mismo. Va
     * en píxeles medidos, no en porcentaje: es lo que vale un rem aquí, y el
     * documento no puede adivinarlo desde dentro.
     */
    const palette1 = computed.getPropertyValue("--palette-1").trim();
    if (palette1) vars["--palette-1"] = palette1;
    vars["font-size"] = computed.fontSize;
    // La sombra de la barra de arriba (ver `global.css`). Va siempre, también
    // a cero: al ensanchar la ventana la barra se va.
    vars["--plane-topbar-shadow"] =
      computed.getPropertyValue("--plane-topbar-shadow").trim() || "0px";
    // El esquema de color lo fija el tema activo (el del panel o el del
    // contenedor de la aplicación, según donde viva este marco).
    return {
      vars,
      modo: computed.colorScheme === "dark" ? "dark" : "light",
      // La paleta de la aplicación, para que el documento la ponga en su raíz
      // y las hojas deriven ahí dentro. Ver `server/page/pageStyles.ts`.
      paleta:
        (element as HTMLElement).closest("[data-palette]")?.getAttribute("data-palette") ?? null,
    };
  }

  /**
   * Aplica el tema al principio del srcdoc para evitar mostrar un fondo claro antes de
   * que el puente avise de que está listo.
   *
   * Solo lo inserta al cargar el documento. Los cambios posteriores se envían por mensaje
   * porque modificar srcdoc reiniciaría la página.
   */
  function conTema(html: string, element: Element): { html: string; modo: "light" | "dark" } {
    const datos = readTheme(element);
    // `<` escapado: un `</script` dentro de los datos cerraria la etiqueta.
    const carga = JSON.stringify(datos).replace(/</g, "\\u003c");
    const guion =
      `<script data-plane="tema-inicial">(function(){var d=${carga},r=document.documentElement;` +
      `for(var k in d.vars)r.style.setProperty(k,d.vars[k]);r.style.colorScheme=d.modo;` +
      `r.setAttribute("data-theme",d.modo);r.setAttribute("data-plane-tema",d.modo);` +
      // La barra va como `\u002f`: escrito entero, el cierre acabaría con
      // este mismo bloque al leer el componente.
      `if(d.paleta)r.setAttribute("data-palette",d.paleta)})()<\u002fscript>`;

    /*
     * Lo antes posible, pero nunca delante del doctype: un guion antes de el
     * manda al navegador a modo peculiar y el documento se dibuja con otras
     * reglas de caja.
     */
    const donde =
      /<head\b[^>]*>/i.exec(html) ??
      /<html\b[^>]*>/i.exec(html) ??
      /^\s*<!doctype[^>]*>/i.exec(html);
    const texto = donde
      ? html.slice(0, donde.index + donde[0].length) +
        guion +
        html.slice(donde.index + donde[0].length)
      : guion + html;
    return { html: texto, modo: datos.modo };
  }
</script>

<script lang="ts">
  import { personDisplayName } from "@shared/people";
  import {
    CONNECTOR_IN_REVIEW,
    CONNECTOR_UNAVAILABLE,
    NEEDS_SESSION,
    type PageIssue,
    type PickedBlock,
    SOURCE_BROKEN,
  } from "@shared/types";
  import { untrack } from "svelte";

  import { cx } from "../lib/cx";
  import { fontSize } from "../lib/fontSize.svelte";
  import { FRAME_DRAG, FRAME_DROP } from "../lib/frameEvents";
  import type { DocLoader } from "../lib/htmlDocs";
  import { nextPickId } from "../lib/pagePicker.svelte";
  import { errorMessage, jsonBody } from "../lib/pb";
  import { getPeople } from "../lib/people.svelte";
  import type { BrokenSource } from "../lib/quickAsk";
  import { theme } from "../lib/theme.svelte";
  import Toast from "./Toast.svelte";

  let {
    doc: docHash,
    sources: declared,
    maxHeight,
    fill = false,
    themeKey,
    appId,
    pageId,
    previewRole,
    previewPerson,
    previewAnon = false,
    previewNoRole = false,
    client,
    loadDoc,
    title,
    roles,
    onStorage,
    onNavigate,
    picking = false,
    onPick,
    onPickerOff,
    onIssue,
    onReady,
    onRead,
    onViewerRead,
    onBrokenSource,
    dryRun = false,
  }: {
    /** Huella del documento que hay que dibujar. Vacía: no hay nada que dibujar. */
    doc?: string;
    /** Tablas que ese documento puede pedir. Nada más está permitido. */
    sources?: HtmlSource[];
    /** Tope de alto en píxeles. Solo cuenta cuando el marco crece con su contenido. */
    maxHeight?: number;
    /**
     * El marco ocupa todo el alto de donde vive, en vez de crecer con su
     * contenido. Es lo que necesita una página entera.
     */
    fill?: boolean;
    /**
     * Cambia cuando cambia la paleta de la aplicación. Sirve para volver a
     * mandar los colores sin que el marco tenga que saber de donde salen.
     */
    themeKey?: string;
    /** Aplicación y página a las que el documento pide datos. */
    appId: string;
    pageId: string;
    /**
     * Mirar la página como lo haría alguien con este rol. Solo lo atiende el
     * servidor si quien lo pide es el dueño de la aplicación, y nunca amplía lo
     * que ese dueño ya alcanza.
     */
    previewRole?: string;
    /**
     * Persona concreta con la que mirar, de entre las que tienen ese rol. Es lo
     * que permite probar una pantalla que enseña "lo mío".
     *
     * Viaja entera y no solo su identificador: al servidor le basta el id, pero
     * el documento necesita también el nombre, el correo, sus roles de verdad y
     * sus columnas propias para que `plane.usuario` sea esa persona y no quien
     * construye.
     */
    previewPerson?: AppPerson | null;
    /** Mirar la página como la vería alguien que llega sin cuenta. */
    previewAnon?: boolean;
    /**
     * Mirar la página como la vería alguien que entró y no tiene ningún rol de
     * la aplicación. Hay sesión, y no hay rol: no es lo mismo que `previewAnon`
     * ni que un rol vacío, y por eso viaja aparte hasta el servidor.
     */
    previewNoRole?: boolean;
    client: PocketBase;
    loadDoc?: DocLoader;
    title?: string;
    /** Roles de quien está mirando, para que el contenido sepa a quien tiene delante. */
    roles?: string[];
    /** El contenido guardó algo en el almacenamiento de mentira del marco. */
    onStorage?: () => void;
    /**
     * El contenido intentó irse a otra página. `stuck` cuando ya agoto las
     * vueltas y el marco se quedó quieto.
     */
    onNavigate?: (info: { stuck: boolean }) => void;
    /**
     * El cursor de selección está encendido: pasar el ratón por dentro del
     * documento ilumina lo que se seleccionaria, y el clic se consume.
     */
    picking?: boolean;
    /** Se senalo un elemento del documento. */
    onPick?: (block: PickedBlock) => void;
    /** El documento apago el cursor por su cuenta, con escape. */
    onPickerOff?: () => void;
    /**
     * El documento soltó un fallo: lo mismo que se vería en la consola del
     * navegador. Llega tal cual, sin filtrar y sin agrupar.
     */
    onIssue?: (issue: PageIssue | { tipo: "aviso"; mensaje: string }) => void;
    /** El puente ya está en pie dentro del marco. */
    onReady?: () => void;
    /**
     * El documento leyó de una tabla y le llegó esto. Es para la prueba de la
     * IA: saber que filas llegaron es lo que deja ver si luego se pintaron.
     */
    onRead?: (read: ProbeRead) => void;
    /**
     * El documento consultó `plane.usuario`: lo que enseña depende de quien
     * entra. Avisa una vez por documento.
     */
    onViewerRead?: () => void;
    /**
     * El documento pidió una tabla o una columna que su manifiesto no cubre: la
     * fuente no está declarada, o su tabla ya no existe. Es lo que deja ofrecer
     * «Reparar conexión». La orden se rechaza igual, y la página pinta su aviso
     * como en la aplicación publicada.
     */
    onBrokenSource?: (broken: BrokenSource) => void;
    /**
     * Ensayo: las órdenes que escriben --crear, actualizar, borrar-- se
     * responden con un `ok` de mentira, sin llegar al servidor. Solo pasan las
     * de lectura.
     *
     * Es para probar una página recién escrita: si su código guarda algo al
     * cargarse, la prueba no puede dejar ese rastro en los datos de verdad.
     */
    dryRun?: boolean;
  } = $props();

  /*
   * El padrón de la aplicación, para poder decir el nombre de quien mira.
   * La cuenta no lo sabe --guarda el correo sin el dominio-- y la fila de esa
   * persona en la tabla de personas si. Ver `viewer`.
   *
   * Se coge del contexto, que lo pone la pantalla que abre la aplicación: el
   * panel con la lista del constructor y la aplicación publicada con la suya.
   * Donde no lo haya --el marco de una conversión, la sonda-- llega vacío y el
   * nombre se cae a la cuenta, que es lo que se hacía antes.
   */
  const people = getPeople();

  let frame = $state<HTMLIFrameElement | null>(null);
  let holder = $state<HTMLDivElement | null>(null);
  let doc = $state("");
  /*
   * Claro u oscuro, tal como lo ve este marco. Lo usa el elemento del marco
   * para que el hueco que ocupa mientras el documento se carga sea del color
   * que toca: sin esto el navegador lo pinta blanco, y en oscuro se ve un
   * parpadeo en cada cambio de página. Lo de dentro lo pone `conTema`.
   */
  let modo = $state<"light" | "dark">("light");
  let height = $state(START_HEIGHT);
  let ready = $state(false);
  let error = $state("");
  /**
   * El aviso flotante de la plataforma sobre la página: hace falta iniciar
   * sesión para guardar, un conector ya no está disponible o está en
   * revisión.
   *
   * Lo pone la plataforma y no el HTML de la página: así no se puede saltar y
   * no depende de que quien escribió la página se acordara. Ver `design.md` D4.
   */
  let platformNotice = $state({ text: "", turno: 0 });

  /** Los códigos de error que la plataforma avisa sobre la página. */
  const NOTICE_CODES = new Set([NEEDS_SESSION, CONNECTOR_UNAVAILABLE, CONNECTOR_IN_REVIEW]);
  /** Órdenes de datos que el documento pidió y aún no tienen respuesta. */
  const inFlight = new Set<symbol>();

  /**
   * Cuántas órdenes siguen en camino. La prueba de la IA espera a que lleguen
   * antes de mirar la pantalla: con el servidor ocupado, la primera consulta
   * podía tardar más que el rato de gracia y la página se daba por vacía.
   */
  export function pendingCalls(): number {
    return inFlight.size;
  }

  /** Vistazos pedidos al documento que aún no contestó. */
  const glances = new Map<string, (view: ProbeView | null) => void>();
  let glanceCount = 0;

  /**
   * Lo que el documento enseña ahora mismo: filas de sus tablas, puntos de sus
   * gráficas, cifras y avisos a la vista. Lo usa la prueba de la IA para ver si
   * los datos que se leyeron llegaron a la pantalla. `null` si el documento no
   * contesta en un segundo.
   */
  export function glance(): Promise<ProbeView | null> {
    const win = frame?.contentWindow;
    if (!win) return Promise.resolve(null);
    const id = `v${++glanceCount}`;
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        glances.delete(id);
        resolve(null);
      }, 1000);
      glances.set(id, (view) => {
        clearTimeout(timer);
        glances.delete(id);
        resolve(view);
      });
      win.postMessage({ plane: "vistazo", id }, "*");
    });
  }

  /** Cambia para volver a montar el marco cuando el documento se va. */
  let turn = $state(0);
  /** El documento agoto sus vueltas: se retira el marco y se deja quieto. */
  let stuck = $state(false);

  const sources = $derived(declared ?? []);

  /** Vueltas gastadas por un documento que insiste en irse. */
  let vueltas = 0;
  let asentado: ReturnType<typeof setTimeout> | undefined;

  /*
   * La huella y el cargador, por valor.
   *
   * Una prop como `doc={page.doc}` es un captador: el efecto que la lee
   * depende también de `page`, y la página se vuelve a leer entera a menudo
   * --al terminar la IA, al guardar su memoria, cuando otra conversación
   * cambia algo--. Leída a pelo, cada relectura vaciaba el marco y lo volvía
   * a cargar aunque la huella fuera la misma, y se perdía lo escrito en un
   * formulario. Un `$derived` solo avisa si el valor cambia.
   */
  const hashActual = $derived(docHash);
  const cargador = $derived(loadDoc);

  /* Traer el documento. Depende de la huella y de quien lo sepa cargar. */
  $effect(() => {
    const hash = hashActual;
    const load = cargador;
    ready = false;
    error = "";
    inFlight.clear();
    clearTimeout(asentado);
    doc = "";
    height = START_HEIGHT;
    // Otro contenido empieza de cero: las vueltas que gastó el anterior no
    // cuentan contra el.
    stuck = false;
    vueltas = 0;
    if (!hash || !load) return;

    let alive = true;
    load(hash)
      .then((html) => {
        if (!alive) return;
        // Con el tema dentro, para que el documento no se dibuje nunca en
        // claro antes de recibir el mensaje. Ver `conTema`.
        const puesto = holder ? conTema(html, holder) : null;
        if (puesto) modo = puesto.modo;
        doc = puesto?.html ?? html;
        error = "";
      })
      .catch((err) => {
        if (alive) error = errorMessage(err);
      });
    return () => {
      alive = false;
    };
  });

  /**
   * Quien está mirando, tal como lo llama el documento: los cuatro datos de la
   * plataforma y, al mismo nivel, las columnas que la aplicación le haya puesto
   * en su tabla de personas. Ver `design.md` D4 de
   * `relacion-automatica-con-personas`.
   */
  type Viewer = Record<string, unknown> & {
    id: string;
    nombre: string;
    correo: string;
    roles: string[];
  };

  /**
   * Las columnas propias de una persona, listas para viajar.
   *
   * Van planas --`plane.usuario.cedula`, no `plane.usuario.campos.cedula`--
   * porque es lo que se escribe en el filtro de una pantalla de "lo mío".
   *
   * Se esparcen **antes** que los cuatro de la plataforma a propósito: una
   * aplicación de antes de que el nombre se reservara puede tener una columna
   * llamada `correo`, y taparía el correo de la cuenta. Y la columna `nombre`,
   * que toda tabla de personas trae, dice lo mismo que `nombre` pero sin su
   * respaldo al correo cuando está vacía.
   *
   * `$state.snapshot` deja un objeto plano: lo que llega de la lista viene
   * envuelto en el proxy de las runas y `postMessage` no sabe copiarlo.
   */
  function ownColumns(person: AppPerson | null): Record<string, unknown> {
    if (!person?.campos) return {};
    return $state.snapshot(person.campos) as Record<string, unknown>;
  }

  /**
   * Quien está mirando, tal como lo ve el documento.
   *
   * Es la copia de este lado de lo que el servidor compone en `asViewer`, y
   * tiene que decir lo mismo: si no, el documento recibe filas resueltas para
   * una persona y un `plane.usuario` que es otra, y una pantalla de "lo mío"
   * se ve vacía en la vista previa aunque publicada funcione. Ver `design.md`
   * D5 de `permisos-simples-por-rol`.
   *
   * - Sin sesión: nadie, igual que quien llega sin cuenta.
   * - Sin rol y sin persona: hay sesión y no hay ningún rol, que es como entra
   *   quien acaba de ser invitado y todavía no le nombraron uno.
   * - Con persona: esa persona, con su identificador, su nombre, su correo y
   *   sus roles de verdad --no solo el del selector, que es uno de los suyos--.
   * - Con rol y sin persona: hay sesión y no hay identidad concreta. Quien
   *   construye no está en la tabla de personas, así que una pantalla de "lo
   *   mío" se ve vacía; es lo que de verdad pasa, y lo que arregla elegir
   *   persona.
   * - Sin nada de eso --la aplicación publicada--: quien tiene la sesión.
   *
   * Los arrays se copian a mano: llegan envueltos en el proxy de las runas y
   * `postMessage` no sabe copiar un proxy --revienta con `DataCloneError` y el
   * documento se queda sin tema y sin usuario--.
   */
  function viewer(): Viewer | null {
    if (previewAnon) return null;
    if (previewPerson) {
      return {
        ...ownColumns(previewPerson),
        id: previewPerson.id,
        nombre: personDisplayName(previewPerson),
        correo: previewPerson.email ?? "",
        roles: [...previewPerson.roles],
      };
    }
    if (previewRole) return { id: "", nombre: "", correo: "", roles: [previewRole] };
    if (previewNoRole) return { id: "", nombre: "", correo: "", roles: [] };
    // El correo de una cuenta de aplicación está en `cuenta`; el constructor
    // probando su propia página lo tiene donde siempre. Ver `loginFor`.
    const record = client.authStore.record as {
      id: string;
      name?: string;
      cuenta?: string;
      email?: string;
    } | null;
    if (!record) return null;
    /*
     * Y su fila en la tabla de personas, que es donde está su nombre. El
     * `name` de la cuenta no lo es: es el correo sin el dominio, así que quien
     * entra con `1037660432@ss.local` se vería saludado por su cédula. Ver
     * `personDisplayName`.
     *
     * Quien construye no está en ese padrón --no está invitado a su propia
     * aplicación-- y ahí el `name` de la cuenta si es un nombre: es el que se
     * escribió al crearla.
     */
    const mine = people.list.find((p) => p.id === record.id) ?? null;
    return {
      ...ownColumns(mine),
      id: record.id,
      nombre: mine ? personDisplayName(mine) : (record.name ?? ""),
      correo: record.cuenta || record.email || "",
      /*
       * Sin esto, un contenido al que se le prohíbe tener su propio padrón se
       * queda sin distinguir a un administrador.
       */
      roles: $state.snapshot(roles) ?? [],
    };
  }

  /** El ancho del contenedor: ver el efecto que reenvía el tema. */
  let holderWidth = $state(0);

  /** Los colores y quien está mirando, tal como los ve el documento. */
  function sendTheme() {
    const target = frame?.contentWindow;
    if (!target || !holder) return;
    const datos = readTheme(holder);
    // Cambiar de claro a oscuro no recarga el marco --el documento ya está
    // dentro-- pero el hueco del elemento si tiene que cambiar con el.
    modo = datos.modo;
    const { vars, modo: claro, paleta } = datos;
    target.postMessage({ plane: "theme", vars, modo: claro, paleta, usuario: viewer() }, "*");
  }

  /*
   * Lee los estados de tema y paleta para que el efecto se ejecute al cambiarlos, aunque
   * sendTheme obtenga los valores directamente del DOM.
   *
   * Espera al siguiente cuadro para que los efectos que actualizan los atributos y
   * estilos terminen antes de leerlos.
   */
  $effect(() => {
    void theme.name;
    void themeKey;
    void roles;
    /*
     * Y con quien se mira, que también entra en el mensaje. Cambiarlo vuelve a
     * montar el marco desde la escena, así que hoy el tema se reenvía de todos
     * modos; leerlo aquí es lo que hace que `sendTheme` siga diciendo la verdad
     * si algún día deja de remontarse.
     */
    void previewRole;
    void previewPerson;
    void previewAnon;
    void previewNoRole;
    /*
     * El padrón llega después del primer dibujado --se pide al servidor-- y con
     * el llega el nombre de quien mira. Sin leerlo aquí, el documento se
     * quedaría con el que se compuso antes de que existiera: el de la cuenta.
     */
    void people.list;
    // El tamaño de letra del panel también entra en lo que mide `sendTheme`:
    // sin leerlo aquí, cambiarlo no reenviaria el tema y el marco se quedaría
    // con el rem viejo.
    void fontSize.percent;
    /*
     * Y la sombra de la barra de arriba (`--plane-topbar-shadow`), que
     * también viaja en el tema. Cambia cuando la barra de la aplicación
     * publicada aparece o se va --al pasar de columna a barra, o al cruzar el
     * ancho de un teléfono--, y las dos cosas cambian también el ancho del
     * marco: es lo que se puede observar, porque una variable no avisa.
     */
    void holderWidth;
    if (!ready) return;
    /*
     * Con respaldo de reloj: en una pestaña en segundo plano el navegador no
     * corre `requestAnimationFrame`, el documento nunca recibía el tema, nunca
     * se anunciaba `plane:listo` y no pedía sus datos. Pasaba en la prueba de
     * la IA cuando quien construye se iba a otra pestaña mientras esperaba.
     */
    let sent = false;
    const send = () => {
      if (sent) return;
      sent = true;
      sendTheme();
    };
    const raf = requestAnimationFrame(send);
    const timer = setTimeout(send, 100);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  });

  /*
   * El modo cursor. Se manda también cuando el marco vuelve a estar listo: un
   * documento recién cargado no sabe nada de lo que se pidió antes de existir.
   */
  $effect(() => {
    const activo = picking;
    if (!ready) return;
    frame?.contentWindow?.postMessage({ plane: "cursor", activo }, "*");
  });

  /*
   * Las flechas que ensanchan lo señalado. El puente ya las escucha dentro del
   * documento, pero el teclado casi nunca está ahí: quien enciende el cursor lo
   * hace desde el panel y sigue con el foco en lo que estaba escribiendo, así
   * que las pulsaciones se quedan de este lado. Se recogen aquí y se pasan.
   */
  $effect(() => {
    if (!picking) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
      e.preventDefault();
      frame?.contentWindow?.postMessage(
        { plane: "cursor-nivel", paso: e.key === "ArrowUp" ? 1 : -1 },
        "*",
      );
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  });

  /*
   * El oyente de mensajes se registra una sola vez. Si se quitara y se volviera
   * a poner con cada cambio, un mensaje que llegara justo en ese hueco se
   * perderia, y el puente no repite un valor que ya mandó.
   *
   * Todo lo que lee de fuera va dentro de `untrack`: son las variables vivas
   * del componente --el cierre siempre ve las de ahora-- y leerlas aquí de
   * forma rastreada volveria a montar el oyente con cada cambio, que es justo
   * lo que se quiere evitar.
   */
  $effect(() => {
    const onMessage = (event: MessageEvent) => {
      // Un marco sin origen propio se anuncia como "null": la única forma
      // fiable de reconocerlo es por su ventana.
      const node = untrack(() => frame);
      if (!node || event.source !== node.contentWindow) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;

      if (data.plane === "ready") {
        ready = true;
        untrack(() => onReady)?.();
        return;
      }

      /* Lo que el documento rompio, tal como saldría en la consola. */
      if (data.plane === "fallo") {
        const fallo = data.fallo;
        if (!fallo || typeof fallo !== "object") return;
        untrack(() => onIssue)?.({
          tipo: fallo.tipo,
          mensaje: String(fallo.mensaje ?? "").slice(0, 1000),
          ...(fallo.linea ? { linea: Number(fallo.linea) } : {}),
          ...(fallo.columna ? { columna: Number(fallo.columna) } : {}),
          ...(fallo.pila ? { pila: String(fallo.pila) } : {}),
        });
        return;
      }

      if (data.plane === "usuario-leido") {
        untrack(() => onViewerRead)?.();
        return;
      }

      if (data.plane === "vistazo") {
        glances.get(String(data.id))?.(
          data.vista && typeof data.vista === "object" ? (data.vista as ProbeView) : null,
        );
        return;
      }

      if (data.plane === "storage") {
        untrack(() => onStorage)?.();
        return;
      }

      /*
       * Alguien arrastra un archivo por encima del documento. El marco no sabe
       * que hacer con el --eso lo decide el editor-- así que solo repite el
       * aviso en la ventana: quien tenga la zona de soltar lo recogera.
       */
      if (data.plane === "arrastre") {
        window.dispatchEvent(new CustomEvent(FRAME_DRAG));
        return;
      }

      if (data.plane === "soltar") {
        const archivos = Array.isArray(data.archivos) ? (data.archivos as File[]) : [];
        window.dispatchEvent(new CustomEvent(FRAME_DROP, { detail: { archivos } }));
        return;
      }

      /* Lo señalado con el cursor: donde está y lo que tiene ahora. */
      if (data.plane === "picked") {
        untrack(() => onPick)?.({
          id: nextPickId(),
          name: String(data.nombre ?? ""),
          label: String(data.etiqueta ?? "") || "un elemento",
          tag: String(data.tag ?? "").toLowerCase(),
          path: String(data.ruta ?? ""),
          html: String(data.html ?? ""),
          truncated: data.recortado === true,
        });
        return;
      }

      /* Se salió del cursor desde dentro del documento, con escape. */
      if (data.plane === "picker-off") {
        untrack(() => onPickerOff)?.();
        return;
      }

      /*
       * El contenido intenta salir de la página. Los enlaces y los formularios
       * los para el puente; una asignación directa de la dirección no se
       * puede parar, solo deshacer: el documento vuelve a su sitio.
       */
      if (data.plane === "nav") {
        const saliendo = data.modo === "saliendo";
        const agotado = saliendo && vueltas >= MAX_RESTORES;
        untrack(() => onNavigate)?.({ stuck: agotado });
        if (!saliendo) return;

        /*
         * Agotadas las vueltas se quita el marco. Dejarlo sería dejar dentro
         * la página a la que se fue, que es justo lo que no puede verse: la
         * página de error del navegador.
         */
        if (agotado) {
          stuck = true;
          height = START_HEIGHT;
          return;
        }

        vueltas += 1;
        clearTimeout(asentado);
        // Un documento que aguanta un rato deja de arrastrar sus vueltas: el
        // tope es para el que se va siempre, no para el que se fue una vez.
        asentado = setTimeout(() => {
          vueltas = 0;
        }, SETTLED_MS);

        ready = false;
        inFlight.clear();
        height = START_HEIGHT;
        turn += 1;
        return;
      }

      if (data.plane === "height") {
        const wanted = Number(data.value) || START_HEIGHT;
        const tope = untrack(() => maxHeight);
        height = tope ? Math.min(wanted, tope) : wanted;
        return;
      }

      if (data.plane === "call") {
        // Una respuesta tardía pertenece al documento que la pidió, aunque
        // el mismo componente ya esté mostrando otra página.
        const requestedDoc = untrack(() => doc);
        const requestedTurn = untrack(() => turn);
        const current = () => frame === node && doc === requestedDoc && turn === requestedTurn;
        const reply = (payload: Record<string, unknown>) => {
          if (current())
            node.contentWindow?.postMessage({ plane: "result", id: data.id, ...payload }, "*");
        };

        const call = Symbol();
        inFlight.add(call);
        void (async () => {
          try {
            /*
             * En ensayo, lo que escribe no llega a escribir. Se contesta que
             * salió bien para que la página siga su curso y se pueda ver como
             * se comporta entera, pero no se toca ni un dato.
             */
            if (untrack(() => dryRun) && WRITE_OPS.has(String(data.op))) {
              reply({ ok: true, data: { id: "ensayo", ensayo: true } });
              return;
            }
            const op = String(data.op);
            const result = await runOp(op, data.args ?? [], {
              sources: untrack(() => sources),
              client: untrack(() => client),
              appId: untrack(() => appId),
              pageId: untrack(() => pageId),
              previewRole: untrack(() => previewRole),
              previewPerson: untrack(() => previewPerson),
              previewAnon: untrack(() => previewAnon),
              previewNoRole: untrack(() => previewNoRole),
              dryRun: untrack(() => dryRun),
            });
            if (!current()) return;
            reply({ ok: true, data: result });
            const read = readOf(op, data.args, result);
            if (read) untrack(() => onRead)?.(read);
          } catch (err) {
            if (!current()) return;
            const codigo = err instanceof OpError ? err.codigo : "";
            // El aviso lo pone la plataforma; la orden se rechaza igual, para
            // que la página pueda capturarla si quiere hacer algo mejor.
            // El turno hace que dos intentos seguidos vuelvan a asomar el
            // aviso aunque el mensaje sea el mismo.
            if (NOTICE_CODES.has(codigo)) {
              platformNotice = { text: errorMessage(err), turno: platformNotice.turno + 1 };
            }
            // Un conector que ya no está deja a la página sin esos datos: la
            // prueba de la IA tiene que enterarse, igual que de una tabla rota.
            if (codigo === CONNECTOR_UNAVAILABLE) {
              untrack(() => onIssue)?.({
                tipo: "consola",
                mensaje: `plane.conector was rejected: ${errorMessage(err)}`,
              });
            } else if (!NOTICE_CODES.has(codigo)) {
              // La página suele capturar el rechazo y pintarlo, así que la
              // consola queda limpia y la prueba diría que todo fue bien. Se
              // cuenta aparte: una consulta rechazada es una página rota.
              untrack(() => onIssue)?.({
                tipo: "consola",
                mensaje: `plane.${String(data.op)} was rejected: ${errorMessage(err)}`,
              });
              if (codigo === SOURCE_BROKEN) {
                // La tabla que pidió es el primer argumento de toda orden de datos.
                const asked = Array.isArray(data.args) ? data.args[0] : "";
                untrack(() => onBrokenSource)?.({
                  tabla: String(asked ?? ""),
                  mensaje: errorMessage(err),
                });
              }
            }
            reply({ ok: false, error: errorMessage(err), codigo: codigo || undefined });
          } finally {
            inFlight.delete(call);
          }
        })();
      }
    };

    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(asentado);
      for (const resolve of glances.values()) resolve(null);
      inFlight.clear();
    };
  });
</script>

<!--
  El aviso de la plataforma sobre la página: hace falta iniciar sesión para
  guardar, o un conector no está disponible o está en revisión. Va fuera del `if` de
  abajo porque la orden que lo dispara sale del marco, que solo existe en la
  otra rama, y porque el aviso vive en su propia capa. Ver `Toast`.
-->
{#if platformNotice.text}
  <Toast kind="warning" key={String(platformNotice.turno)}>{platformNotice.text}</Toast>
{/if}

{#if error}
  <div
    bind:this={holder}
    class={cx(
      "error-frame-html flex items-center justify-center text-center",
      fill && "frame-error-fill",
    )}
  >
    {error}
  </div>
{:else}
  <div
    bind:this={holder}
    bind:clientWidth={holderWidth}
    class={cx("holder-frame-html", fill && "frame-holder-fill")}
  >
    {#if doc && !stuck}
      <!--
        La clave vuelve a montar el marco: así se deshace la salida del
        documento y vuelve a cargarse el contenido de verdad.
      -->
      {#key turn}
        <!--
          `allow-forms` hace falta para que la página reciba el evento
          `submit`: sin él, el navegador cancela el envío antes de avisar y
          un formulario que escucha `submit` no hace nada. El envío nativo
          no llega a salir nunca: lo para el puente (ver htmlBridge.ts).
        -->
        <iframe
          bind:this={frame}
          title={title || "Contenido de la página"}
          srcdoc={doc}
          sandbox="allow-scripts allow-forms"
          style={`color-scheme: ${modo};${fill ? "" : ` height: ${height}px;`}`}
          class={cx("frame-html block w-full", fill && "frame-fill")}
        ></iframe>
      {/key}
    {/if}
  </div>
{/if}

<style>
  .error-frame-html {
    background: transparent;
    border: var(--border-width) dashed var(--border);
    border-radius: var(--radius-sm);
    padding: var(--sp-16) var(--sp-24);
    font-size: var(--text-sm);
    line-height: var(--text-sm--line-height);
    color: var(--text-muted);

    &.frame-error-fill {
      height: 100%;
    }
  }

  /*
   * El contenedor es quien resuelve cuanto vale un rem dentro del documento:
   * `readTheme` mide su `font-size` y lo manda como el de la raíz de la
   * página. Sin esta línea heredaba el del panel --`body` está en
   * `--text-sm`-- y la página entera se dibujaba al 87,5%: un campo del
   * catálogo salía a 12,25px y su etiqueta a 10,5px. Con `1rem` la medida
   * sale de la raíz del panel, y `--font-scale` añade el tamaño propio de la
   * aplicación cuando lo tiene (ver `lib/appTheme.ts`).
   */
  .holder-frame-html {
    font-size: calc(1rem * var(--font-scale));

    &.frame-holder-fill {
      height: 100%;
    }
  }

  .frame-html {
    border: 0;
    /*
     * El color del hueco mientras el documento se carga. `color-scheme` va en
     * línea, con el modo de quien mira: los dos juntos son lo que evita el
     * blanco de fábrica del navegador al montar un marco nuevo.
     */
    background: var(--bg-level1);

    &.frame-fill {
      height: 100%;
    }
  }
</style>
